/**
 * El botón "Agregar al carrito" con sus variantes y la cantidad.
 *
 * Client Component: es la única parte de la página de producto que
 * necesita estado. El carrito vive en `localStorage` (clave
 * `joyeria.carrito`) y el botón escribe ahí; el resto de la página es
 * server-rendered.
 *
 * Al agregar, el contador del header se actualiza con un CustomEvent para
 * que el cliente no tenga que recargar.
 */
'use client';

import { useEffect, useRef, useState } from 'react';

import { icons } from '@/components/icons';
import { Text3DFlip } from '@/components/text-3d-flip';

export interface VarianteParaElegir {
  id: string;
  medida?: number | null;
  largoCm?: number | null;
  material?: string | null;
  stock: number;
}

export interface GrupoVariantes {
  eje: string;
  etiqueta: string;
  variantes: VarianteParaElegir[];
}

export interface Props {
  productoSlug: string;
  grupos: GrupoVariantes[];
  agotado: boolean;
  sinVariantes: boolean;
  /** El texto del botón cuando no hay stock. Lo manda la API. */
  textoAgotado: string;
}

const CLAVE_CARRITO = 'joyeria.carrito';

/** El texto del botón de una variante (misma regla que el legacy). */
function textoVariante(grupo: GrupoVariantes, variante: VarianteParaElegir, materiales: { slug: string; nombre: string }[]): string {
  if (grupo.eje === 'medida') return `N° ${variante.medida}`;
  if (grupo.eje === 'largoCm') return `${variante.largoCm} cm`;
  const material = materiales.find((m) => m.slug === variante.material);
  return material?.nombre ?? (variante.material ?? '').replace(/-/g, ' ');
}

/** Lee el carrito del `localStorage`. */
function leerCarrito(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const crudo = window.localStorage.getItem(CLAVE_CARRITO);
    return crudo ? (JSON.parse(crudo) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

export function AddToCart({ productoSlug, grupos, agotado, sinVariantes, textoAgotado }: Props) {
  const [cantidad, setCantidad] = useState(1);
  const [elegidas, setElegidas] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [agregado, setAgregado] = useState(false);
  const materialsRef = useRef<{ slug: string; nombre: string }[]>([]);

  // Los materiales llegan por props en el server; acá sólo se necesitan los
  // nombres para armar el texto de las variantes.
  useEffect(() => {
    // Se leen del DOM para no duplicar el payload: los botones ya traen el
    // texto final del servidor.
    const textos = Array.from(document.querySelectorAll('[data-variant-texto]'));
    const mapa: { slug: string; nombre: string }[] = [];
    textos.forEach((nodo) => {
      const slug = nodo.getAttribute('data-variant-material') ?? '';
      const nombre = nodo.getAttribute('data-variant-texto') ?? '';
      if (slug) mapa.push({ slug, nombre });
    });
    materialsRef.current = mapa;
  }, []);

  const hayVariantes = grupos.length > 0;

  function alternar(grupo: GrupoVariantes, variante: VarianteParaElegir) {
    setElegidas((previas) => ({ ...previas, [grupo.eje]: variante.id }));
    setError('');
  }

  function agregar() {
    if (agotado || sinVariantes) return;

    // Si hay varios ejes, hay que haber elegido uno de cada.
    const faltante = grupos.find((grupo) => !elegidas[grupo.eje]);
    if (faltante) {
      setError(`Elegí ${faltante.etiqueta.toLowerCase()} antes de agregar al carrito.`);
      return;
    }

    const idVariante = grupos.length === 1 ? elegidas[grupos[0].eje] : undefined;
    const clave = idVariante ? `${productoSlug}:${idVariante}` : productoSlug;

    const carrito = leerCarrito();
    carrito[clave] = (carrito[clave] ?? 0) + cantidad;
    try {
      window.localStorage.setItem(CLAVE_CARRITO, JSON.stringify(carrito));
    } catch {
      setError('No pudimos guardar el carrito. Revisá el almacenamiento del navegador.');
      return;
    }

    // Avisa al header para que actualice el contador.
    window.dispatchEvent(new CustomEvent('joyeria:carrito', { detail: { ...carrito } }));
    setAgregado(true);
    setError('');
    window.setTimeout(() => setAgregado(false), 2200);
  }

  return (
    <div className="product-form">
      {hayVariantes ? (
        grupos.map((grupo) => (
          <div className="option-group" key={grupo.eje}>
            <p className="option-group__label" id={`eje-${grupo.eje}`}>
              {grupo.etiqueta}
            </p>
            <div className="option-group__values" role="group" aria-labelledby={`eje-${grupo.eje}`}>
              {grupo.variantes.map((variante) => {
                const texto = textoVariante(grupo, variante, materialsRef.current);
                const sinStock = variante.stock <= 0;
                return (
                  <button
                    key={variante.id}
                    className="option-value"
                    type="button"
                    data-variant={variante.id}
                    data-eje={grupo.eje}
                    data-stock={variante.stock}
                    data-variant-texto={texto}
                    {...(grupo.eje === 'material' && variante.material
                      ? { 'data-variant-material': variante.material }
                      : {})}
                    aria-pressed={elegidas[grupo.eje] === variante.id}
                    onClick={() => alternar(grupo, variante)}
                    {...(sinStock ? { disabled: true, 'aria-disabled': true as const } : {})}
                  >
                    {texto}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      ) : null}

      {sinVariantes && (
        <p className="product__stock product__stock--out">Este producto no tiene variantes disponibles.</p>
      )}

      {error && (
        <p className="product__stock" data-variant-error role="alert" style={{ color: 'var(--color-fg)', fontWeight: 500 }}>
          {error}
        </p>
      )}

      <div className="btn-row">
        <div className="qty">
          <button
            className="qty__btn"
            type="button"
            onClick={() => setCantidad((n) => Math.max(1, n - 1))}
            aria-label="Restar una unidad"
          >
            {icons.menos}
          </button>
          <span className="qty__value" aria-live="polite">
            {cantidad}
          </span>
          <button
            className="qty__btn"
            type="button"
            onClick={() => setCantidad((n) => n + 1)}
            aria-label="Sumar una unidad"
          >
            {icons.mas}
          </button>
        </div>
        <button
          className={['btn', 'btn--flip', agregado ? 'btn--flip--activo' : ''].filter(Boolean).join(' ')}
          type="button"
          data-add-button
          onClick={agregar}
          {...(agotado || sinVariantes ? { disabled: true } : {})}
        >
          {agregado ? (
            <span className="btn__con-icono">
              <Text3DFlip rotateDirection="top">Agregado</Text3DFlip>
              {icons.check}
            </span>
          ) : agotado ? (
            <Text3DFlip rotateDirection="top">{textoAgotado}</Text3DFlip>
          ) : (
            <Text3DFlip rotateDirection="top">Agregar al carrito</Text3DFlip>
          )}
        </button>
      </div>
    </div>
  );
}
